import { memoizeRemoveable } from "./map";

export function memoizeAsync<K, V>(
  fn: (key: K) => Promise<V>,
): (key: K) => Promise<V> {
  return memoizeRemoveable(async (key, remove) => {
    try {
      return await fn(key);
    } catch (e) {
      remove();
      throw e;
    }
  });
}

type AsyncFunction<TArgs extends any[], T> = (...args: TArgs) => Promise<T>;

export function memoizeAsyncV2<TArgs extends any[], T>(
  fn: AsyncFunction<TArgs, T>,
  options: { cacheSize?: number; keyFn?: (args: TArgs) => string },
): AsyncFunction<TArgs, T> {
  const cache = new Map<string, Promise<T>>();
  const keys: string[] = [];

  return async (...args: TArgs): Promise<T> => {
    const key = (options.keyFn ?? JSON.stringify)(args);

    if (!cache.has(key)) {
      if (cache.size >= (options.cacheSize ?? Infinity)) {
        const oldestKey = keys.shift();
        if (oldestKey) cache.delete(oldestKey);
      }
      const resultPromise = fn(...args);
      cache.set(key, resultPromise);
      keys.push(key);
    }

    return cache.get(key)!;
  };
}
